import { useEffect, useMemo, useRef, useState } from "react";
import { api, type CommandStatus } from "../lib/api";
import { usePendingApprovals } from "../lib/approvalsFeed";
import { heldKeys, useCommandStatus } from "../lib/commandFeed";
import { AskCard } from "../studio/AskCard";
import { toolIndex, type ToolIndex } from "../studio/ask";

export interface SurfaceSubmission { id: string; label: string; source?: "surface_intent" }
// `stopped` is terminal (charter 7.169): somebody ended the turn, so there
// is nothing left to wait for.
const terminal = new Set(["completed", "stopped", "failed", "unknown"]);

/**
 * An accepted click stays beside its surface until its actual result arrives.
 *
 * A plain success then leaves nothing behind: the page hears it through
 * `onStatus` and says so in its own words. A line of ours as well was the same
 * news told twice -- one press of 本版可发布 used to show four confirmations.
 *
 * Told, not asked (CHARTER §7.194): one read on mount, then the `command`
 * frames the server pushes when the status moves. It used to ask again every
 * 1200 ms -- and read the whole approvals list each time -- for as long as the
 * action was open.
 */
export function SurfaceAction({ submission, onStatus, onToast, active = true, earlier = false }: {
  submission: SurfaceSubmission;
  onStatus: (status: CommandStatus) => void;
  onToast: (message: string, tone: "good" | "critical") => void;
  active?: boolean;
  earlier?: boolean;
}) {
  const { status, error, retry } = useCommandStatus(submission.id, active);
  const [tools, setTools] = useState<ToolIndex>({});
  const report = useRef(onStatus);
  report.current = onStatus;
  useEffect(() => {
    if (status) report.current(status);
  }, [status]);
  useEffect(() => {
    if (!active) return;
    let live = true;
    api.capabilities().then(body => live && setTools(toolIndex(body.registries))).catch(() => {});
    return () => { live = false; };
  }, [active]);
  // The cards holding this action: the command frame names their keys, and
  // the cards themselves come off the one pending list the server pushes
  // (§7.194 ⑤) -- nothing is fetched here, and an answer shows the moment
  // the next frame says so.
  const done = Boolean(status?.known && status.action_state && terminal.has(status.action_state));
  const holding = done ? "" : heldKeys(status).join(",");
  const { pending } = usePendingApprovals(active && submission.source !== "surface_intent" && Boolean(holding));
  const cards = useMemo(() => {
    if (!holding || !pending) return [];
    const keys = new Set(holding.split(","));
    return pending.filter(card => keys.has(card.key) || (card.effect_id && keys.has(`effect:${card.effect_id}`)));
  }, [holding, pending]);
  // Message progress goes back to the plugin's controls; approvals remain in
  // the original conversation instead of being duplicated above the workspace.
  if (submission.source === "surface_intent") return null;
  if (status?.known && status.action_state === "completed") return null;
  // Two different endings, and only one of them is a failure.  `failed` is a
  // tool answering no, in the sentence it wrote for the person.  `unknown` is
  // the core saying it cannot tell: the handler raised after it may already
  // have written, and what the effect carries then is the exception's own
  // text (`IntegrityError: UNIQUE constraint failed: …`), written for whoever
  // debugs it.  Calling that 未完成 was wrong twice -- it may well have
  // happened -- and printing it put a stack's vocabulary on an owner's page.
  // The raw text stays where it belongs, behind 查看执行过程.
  const unsure = Boolean(status?.known && status.action_state === "unknown");
  const failed = Boolean(status?.known && status.action_state === "failed");
  const effectError = status?.effects.find(effect => effect.error)?.error;
  const said = error || (unsure ? "可能已经生效，先核对再重试。" : effectError);
  const body = <section className="surface-action" aria-label={submission.label}
    data-state={error ? "unreadable" : status?.known ? status.action_state ?? "received" : "received"}>
    <p role="status" className="surface-action-line">
      <strong>{submission.label}</strong>
      <span>{error ? "暂时查不到结果" : unsure ? "结果不确定" : failed ? "未完成"
        : status?.action_state === "stopped" ? "已停下" : cards.length ? "需要你确认" : "处理中"}</span>
    </p>
    {said ? <p role="alert">{said}</p> : null}
    {error || unsure ? <button type="button" className="btn" data-action={unsure ? "check" : "retry"} onClick={retry}>{unsure ? "核对结果" : "重试"}</button> : null}
    {cards.length ? <div className="own surface-confirmations">{cards.map(card => <AskCard key={card.key} card={card} tools={tools} onToast={onToast}
      onDone={() => {}} />)}</div> : null}
  </section>;
  return earlier && failed ? <details className="surface-action-history"><summary>此前的“{submission.label}”未完成</summary>{body}</details> : body;
}
